import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { Button, Dropdown, ToastContainer } from "pepsico-ds";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useProductGroups } from "../../hooks/useProductGroups";
import "./productManagement.scss";

const ProductGroupModal = ({
  isOpen,
  onClose,
  onSave,
  onGroupCreated,
  preSelectedProductIds = [],
  defaultMode = "existing",
}) => {
  const [mode, setMode] = useState(defaultMode);
  const [groupName, setGroupName] = useState("");
  const [selectedExistingGroups, setSelectedExistingGroups] = useState([]);
  const [modalToast, setModalToast] = useState([]);

  useEffect(() => {
    if (isOpen) setMode(defaultMode);
  }, [isOpen, defaultMode]);

  const {
    productGroups: productGroupOptions,
    creating,
    createProductGroup,
  } = useProductGroups();

  const handleClose = useCallback(() => {
    setMode(defaultMode);
    setGroupName("");
    setSelectedExistingGroups([]);
    setModalToast([]);
    onClose();
  }, [onClose, defaultMode]);

  const handleNext = useCallback(async () => {
    if (mode === "existing") {
      if (!selectedExistingGroups || selectedExistingGroups.length === 0) {
        setModalToast([
          {
            text: "Please select at least one product group",
            type: "error",
            id: "select-error",
          },
        ]);
        return;
      }
      if (preSelectedProductIds.length > 0) {
        const groupIds = selectedExistingGroups.map((g) => g.id);
        onSave(groupIds, preSelectedProductIds);
      }
      handleClose();
    } else {
      const name = groupName.trim();
      if (!name) {
        setModalToast([
          { text: "Please enter a group name", type: "error", id: "name-error" },
        ]);
        return;
      }

      const isDuplicate = productGroupOptions.some(
        (g) => g.displayText.toLowerCase() === name.toLowerCase()
      );
      if (isDuplicate) {
        setModalToast([
          {
            text: "A group with this name already exists",
            type: "error",
            id: "dup-error",
          },
        ]);
        return;
      }

      try {
        const result = await createProductGroup(name);
        const newGroupId = result?.data?.createKeywordGroup?.id;
        if (preSelectedProductIds.length > 0 && newGroupId) {
          onSave([newGroupId], preSelectedProductIds);
        }
        if (onGroupCreated) onGroupCreated();
        handleClose();
      } catch {
        setModalToast([
          {
            text: "Failed to create product group. Try again.",
            type: "error",
            id: "save-error",
          },
        ]);
      }
    }
  }, [
    mode,
    groupName,
    selectedExistingGroups,
    preSelectedProductIds,
    productGroupOptions,
    onSave,
    createProductGroup,
    handleClose,
    onGroupCreated,
  ]);

  if (!isOpen) return null;

  return (
    <Dialog
      className="product-group-modal"
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <h3 style={{ margin: 0 }}>Add to Product Group</h3>
          </Grid>
          <Grid item>
            <CloseIcon
              onClick={handleClose}
              className="cursor-pointer"
              data-testid="product-group-modal-close"
            />
          </Grid>
        </Grid>
        <ToastContainer
          data={modalToast}
          removeToast={() => setModalToast([])}
          showActionIcon
        />
      </DialogTitle>
      <DialogContent>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel
            value="existing"
            control={<Radio size="small" />}
            label="Select existing group(s)"
          />
          {mode === "existing" && (
            <div className="product-group-field">
              <Dropdown
                childList={productGroupOptions}
                selection="multiple"
                selectedValue={selectedExistingGroups}
                setSelectedValue={setSelectedExistingGroups}
                placeholder="Select product groups"
                size="medium"
              />
            </div>
          )}

          <FormControlLabel
            value="new"
            control={<Radio size="small" />}
            label="Create new group"
          />
          {mode === "new" && (
            <div className="product-group-field">
              <TextField
                size="small"
                fullWidth
                placeholder="Type group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                inputProps={{ maxLength: 50 }}
              />
            </div>
          )}
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button
          variant="secondary"
          onClick={handleClose}
          text="Cancel"
          size="small"
        />
        <Button
          variant="primary"
          onClick={handleNext}
          text="Save"
          size="small"
          disabled={creating}
        />
      </DialogActions>
    </Dialog>
  );
};

ProductGroupModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onGroupCreated: PropTypes.func,
  preSelectedProductIds: PropTypes.array,
};

export default ProductGroupModal;
